import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../../components/ui/Logo';
import { Rocket, Star, CheckCircle, ArrowRight, Users, Target, Brain, Phone, Mail, MessageSquare, Share2, BarChart3, Calendar, TrendingUp, Headphones, DollarSign, Quote, Menu, X } from 'lucide-react';

const HomeLandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const mainFeatures = [
    {
      id: 1,
      title: "Capture More Leads Instantly",
      description: "Convert more visitors into leads with AI-optimized landing pages, funnels, and forms.",
      benefits: ["Automated lead capture", "Smart follow-ups", "Real-time tracking and reporting"],
      Icon: Target,
      gradient: "from-blue-500 to-cyan-600"
    },
    {
      id: 2,
      title: "Nurture and Engage Automatically",
      description: "Turn your audience into loyal customers using intelligent, multi-channel communication tools — all managed from one dashboard.",
      subFeatures: [
        { name: "Cloud Calling", description: "Connect with leads using a reliable, high-quality calling system.", Icon: Phone },
        { name: "AI Smart Dialer", description: "Automate and optimize calls with AI-powered intelligence.", Icon: Brain },
        { name: "Email Campaigns", description: "Build and automate personalized email marketing campaigns.", Icon: Mail },
        { name: "SMS Messaging", description: "Engage prospects instantly with text message campaigns.", Icon: MessageSquare },
        { name: "Social Media", description: "Reach your audience across multiple social platforms.", Icon: Share2 }
      ],
      gradient: "from-purple-500 to-pink-600"
    },
    {
      id: 3,
      title: "Close More Sales - Effortlessly",
      description: "Centralize every lead, pipeline, and opportunity in one dashboard.",
      benefits: ["Built-in CRM for tracking deals", "Sales automation workflows", "AI insights to boost conversions"],
      Icon: BarChart3,
      gradient: "from-green-500 to-teal-600"
    },
    {
      id: 4,
      title: "Build a Stronger Online Presence",
      description: "Manage all your marketing channels in one place.",
      benefits: ["Website & funnel builder", "Social media scheduler", "Reputation & review management"],
      Icon: Calendar,
      gradient: "from-orange-500 to-red-600"
    }
  ];

  const whyChooseUs = [
    { Icon: CheckCircle, title: "All-In-One Platform", description: "No more juggling 10 different tools. Everything you need in one place.", gradient: "from-blue-500 to-cyan-500" },
    { Icon: Brain, title: "AI-Driven Insights", description: "Smarter automation that adapts to your audience and maximizes results.", gradient: "from-purple-500 to-pink-500" },
    { Icon: TrendingUp, title: "Scalable for Any Business", description: "From freelancers to large agencies — grows with your business.", gradient: "from-green-500 to-teal-500" },
    { Icon: Headphones, title: "24/7 Support & Training", description: "We\'re with you every step of the way with dedicated support.", gradient: "from-orange-500 to-red-500" }
  ];

  const testimonials = [
    { name: "Sarah Johnson", role: "Marketing Director", company: "TechStart Solutions", content: "LeadGen Pro AI transformed our lead generation process. We saw a 300% increase in qualified leads within the first month!", rating: 5 },
    { name: "Mike Chen", role: "Founder & CEO", company: "GrowthHackers Inc", content: "The AI-powered automation saved us 20+ hours per week. Now we can focus on closing deals instead of manual tasks.", rating: 5 },
    { name: "Emily Rodriguez", role: "Sales Manager", company: "Digital Marketing Pro", content: "The all-in-one platform eliminated the need for 8 different tools. Our ROI increased by 250% in just 3 months.", rating: 5 }
  ];

  const stats = [
    { Icon: Users, number: "50,000+", label: "Active Users", gradient: "from-blue-500 to-cyan-500" },
    { Icon: TrendingUp, number: "300%", label: "Average Lead Increase", gradient: "from-green-500 to-teal-500" },
    { Icon: DollarSign, number: "$2M+", label: "Revenue Generated", gradient: "from-purple-500 to-pink-500" }
  ];

  // Pricing plans - EXACTLY matching subscription management page ($163/$384/$520/$649)
  const pricingPlans = [
    {
      name: "Free", price: 0, description: "Perfect for getting started",
      features: ["100 leads/month", "500 emails", "Community support"],
      badge: null,
      cardClass: "bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300",
      textColor: "text-green-900", buttonClass: "bg-green-600 hover:bg-green-700 text-white",
      ctaText: "Start Free", ctaLink: "/register"
    },
    {
      name: "Basic", price: 163, description: "Great for small businesses",
      features: ["1k leads/month", "5k emails", "AI Insights", "Chat & Email support", "+ Many more features"],
      badge: null,
      cardClass: "bg-white border border-gray-200 hover:border-blue-300",
      textColor: "text-blue-900", buttonClass: "bg-blue-600 hover:bg-blue-700 text-white",
      ctaText: "Choose Basic", ctaLink: "/register"
    },
    {
      name: "Professional", price: 384, description: "Most popular for growing teams",
      features: ["5k leads/month", "25k emails", "AI Insights", "Priority Chat & Email", "5 custom integrations", "+ Many more features"],
      badge: "Most Popular", badgeGradient: "from-blue-500 to-purple-600",
      cardClass: "bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-500 ring-4 ring-blue-200 ring-opacity-30",
      textColor: "text-blue-600", buttonClass: "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white",
      ctaText: "Choose Professional", ctaLink: "/register"
    },
    {
      name: "Premium", price: 520, description: "Enterprise-grade solution",
      features: ["15k leads/month", "60k emails", "AI Insights", "Priority Support & Phone", "15 custom integrations", "+ Many more features"],
      badge: "Advanced", badgeGradient: "from-purple-500 to-pink-600",
      cardClass: "bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-400 ring-4 ring-purple-200 ring-opacity-30",
      textColor: "text-purple-600", buttonClass: "bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white",
      ctaText: "Choose Premium", ctaLink: "/register"
    },
    {
      name: "Enterprise", price: 649, description: "For large organizations",
      features: ["25k leads/month", "100k emails", "AI Insights", "Dedicated Support Manager", "Unlimited integrations", "99.9% SLA", "+ Many more features"],
      badge: "Enterprise", badgeGradient: "from-gray-700 to-gray-900",
      cardClass: "bg-white border border-gray-200 hover:border-gray-400",
      textColor: "text-gray-900", buttonClass: "bg-gray-900 hover:bg-gray-800 text-white",
      ctaText: "Choose Enterprise", ctaLink: "/register"
    }
  ];

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ===== HERO SECTION ===== */}
      <section id="hero" className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="absolute top-0 left-0 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        {/* Navigation */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <Logo size="medium" variant="white" showText={true} linkTo="/" />

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('features')} className="text-blue-200 hover:text-white text-sm font-medium transition-colors duration-200">Features</button>
              <button onClick={() => scrollToSection('pricing')} className="text-blue-200 hover:text-white text-sm font-medium transition-colors duration-200">Pricing</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-blue-200 hover:text-white text-sm font-medium transition-colors duration-200">Testimonials</button>
              <Link to="/login" className="text-white hover:text-blue-200 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200">
                Sign In
              </Link>
              <Link to="/register" className="bg-white text-blue-900 hover:bg-blue-50 px-6 py-2 rounded-lg font-semibold transition-colors duration-200 shadow-lg">
                Get Started Free
              </Link>
            </div>

            {/* Mobile Nav Toggle */}
            <div className="md:hidden flex items-center space-x-3">
              <Link to="/login" className="text-white text-sm font-medium">Sign In</Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2 rounded-md hover:bg-white hover:bg-opacity-20 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-blue-900 bg-opacity-95 rounded-xl p-4 mb-4 border border-blue-700 shadow-2xl">
              <div className="flex flex-col space-y-2">
                <button onClick={() => scrollToSection('features')} className="text-blue-200 hover:text-white text-sm font-medium text-left py-2.5 px-3 rounded-lg hover:bg-blue-800 transition-colors">Features</button>
                <button onClick={() => scrollToSection('pricing')} className="text-blue-200 hover:text-white text-sm font-medium text-left py-2.5 px-3 rounded-lg hover:bg-blue-800 transition-colors">Pricing</button>
                <button onClick={() => scrollToSection('testimonials')} className="text-blue-200 hover:text-white text-sm font-medium text-left py-2.5 px-3 rounded-lg hover:bg-blue-800 transition-colors">Testimonials</button>
                <div className="border-t border-blue-700 pt-2 mt-1">
                  <Link to="/register" className="block bg-white text-blue-900 px-4 py-2.5 rounded-lg font-semibold text-center text-sm hover:bg-blue-50 transition-colors" onClick={() => setMobileMenuOpen(false)}>Get Started Free</Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white bg-opacity-20 backdrop-blur-sm border border-white border-opacity-30 mb-8">
              <Rocket className="w-4 h-4 text-yellow-300 mr-2 flex-shrink-0" />
              <span className="text-white text-sm font-medium">Transform the Way You Grow Your Business</span>
            </div>

            <h1 className="text-4xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-6 leading-tight">
              Elevate Your Marketing and Sales with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                LeadGen Pro AI's All-in-One Platform!
              </span>
            </h1>

            <p className="text-xl lg:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto leading-relaxed">
              At LeadGen Pro AI, we help businesses like yours automate marketing,
              streamline sales, and maximize revenue — all within one powerful platform.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 font-bold rounded-xl hover:bg-blue-50 transition-all duration-200 shadow-2xl text-lg"
              >
                Start Free Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-blue-900 transition-all duration-200 text-lg"
              >
                Sign In
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {stats?.map((stat, i) => {
                const StatIcon = stat?.Icon || stat?.icon;
                return (
                  <div key={i} className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-20">
                    <div className={`w-12 h-12 bg-gradient-to-br ${stat?.gradient} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                      {StatIcon && <StatIcon className="w-6 h-6 text-white" />}
                    </div>
                    <p className="text-3xl font-extrabold text-white">{stat?.number}</p>
                    <p className="text-blue-200 text-sm mt-1">{stat?.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      {/* ===== FEATURES SECTION ===== */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Everything You Need to Grow</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">One platform to capture, nurture, and close more leads with AI-powered automation.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainFeatures?.map((feature) => {
              const FeatureIcon = feature?.Icon;
              return (
                <div key={feature?.id} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature?.gradient} rounded-2xl flex items-center justify-center mb-6`}>
                    {FeatureIcon && <FeatureIcon className="w-7 h-7 text-white" />}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature?.title}</h3>
                  <p className="text-gray-600 mb-5">{feature?.description}</p>
                  {feature?.benefits && (
                    <ul className="space-y-2">
                      {feature?.benefits?.map((b, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {feature?.subFeatures && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                      {feature?.subFeatures?.map((sf, i) => {
                        const SfIcon = sf?.Icon;
                        return (
                          <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              {SfIcon && <SfIcon className="w-4 h-4 text-purple-600" />}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-sm">{sf?.name}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{sf?.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* ===== WHY CHOOSE US ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Why Choose LeadGen Pro AI?</h2>
            <p className="text-xl text-gray-600">Built for businesses that want to grow faster with less effort.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs?.map((item, i) => {
              const ItemIcon = item?.Icon;
              return (
                <div key={i} className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className={`w-14 h-14 bg-gradient-to-br ${item?.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    {ItemIcon && <ItemIcon className="w-7 h-7 text-white" />}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item?.title}</h3>
                  <p className="text-gray-600 text-sm">{item?.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* ===== PRICING SECTION ===== */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600">Choose the plan that fits your business needs. No hidden fees.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {pricingPlans?.map((plan, i) => (
              <div
                key={i}
                className={`relative rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${plan?.cardClass}`}
              >
                {plan?.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-lg bg-gradient-to-r ${plan?.badgeGradient} text-white whitespace-nowrap`}>
                      {plan?.badge}
                    </div>
                  </div>
                )}
                <div className="text-center mb-5 pt-2">
                  <h3 className={`text-xl font-bold mb-1 ${plan?.textColor}`}>{plan?.name}</h3>
                  <p className="text-gray-500 text-xs mb-3">{plan?.description}</p>
                  <div className="flex items-end justify-center gap-1">
                    {plan?.price === 0 ? (
                      <span className={`text-3xl font-extrabold ${plan?.textColor}`}>Free</span>
                    ) : (
                      <>
                        <span className="text-gray-500 text-sm mb-1">$</span>
                        <span className={`text-3xl font-extrabold ${plan?.textColor}`}>{plan?.price}</span>
                        <span className="text-gray-500 text-sm mb-1">/mo</span>
                      </>
                    )}
                  </div>
                </div>
                <ul className="space-y-2 mb-6">
                  {plan?.features?.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-xs">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan?.ctaLink}
                  className={`block w-full text-center py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 shadow-md hover:shadow-lg ${plan?.buttonClass}`}
                >
                  {plan?.ctaText}
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-sm mt-8">
            All plans include a 14-day free trial. No credit card required for Free plan.
          </p>
        </div>
      </section>
      {/* ===== TESTIMONIALS ===== */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-xl text-gray-600">Join thousands of businesses growing with LeadGen Pro AI.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials?.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(t?.rating)]?.map((_, ri) => (
                    <Star key={ri} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-blue-200 mb-3" />
                <p className="text-gray-700 mb-6 leading-relaxed">"{t?.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    {t?.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{t?.name}</p>
                    <p className="text-gray-500 text-xs">{t?.role} at {t?.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ===== CTA SECTION ===== */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-purple-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-extrabold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Join 50,000+ businesses using LeadGen Pro AI to generate more leads and close more deals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-10 py-4 bg-white text-blue-900 font-bold rounded-xl hover:bg-blue-50 transition-all duration-200 shadow-2xl text-lg"
            >
              Start Free Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-10 py-4 border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-blue-900 transition-all duration-200 text-lg"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>
      {/* ===== FOOTER ===== */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <Logo size="small" variant="white" showText={true} linkTo="/" />
              <p className="text-sm mt-3 leading-relaxed">AI-powered lead generation platform for modern businesses.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors">Features</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors">Pricing</button></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Get Started</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:support@leadgenproai.us" className="hover:text-white transition-colors">support@leadgenproai.us</a></li>
                <li><a href="tel:+18449781051" className="hover:text-white transition-colors">(844) 978-1051</a></li>
                <li><Link to="/help-support" className="hover:text-white transition-colors">Help Center</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; {new Date()?.getFullYear()} LeadGen Pro AI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeLandingPage;